import { useState } from 'react';
import { Link } from 'react-router';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatBRL, formatDateTime } from '@/lib/format';
import { nomeMetodo, STATUS_LINK, type LinkPagamentoItem, type ParcelaVenda } from '@/lib/pagamentos';

/** Lista de links de pagamento com a situação de cada um (pago, aguardando...). */
export function ListaLinks({ links, onChanged, mostrarConsultor, mostrarCliente = true }: { links: LinkPagamentoItem[]; onChanged: () => void; mostrarConsultor?: boolean; mostrarCliente?: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);

  async function conferir(id: string) {
    setBusy(id);
    setErro(null);
    try {
      await api.post(`/crm/payment-links/${id}/check`);
      onChanged();
    } catch (e) {
      setErro(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function refazer(id: string) {
    setBusy(id);
    setErro(null);
    try {
      await api.post(`/crm/payment-links/${id}/baixa`);
      onChanged();
    } catch (e) {
      setErro(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function copiar(l: LinkPagamentoItem) {
    try {
      await navigator.clipboard.writeText(l.url);
      setCopiado(l.id);
    } catch {
      setErro('Não foi possível copiar o link.');
    }
  }

  if (links.length === 0) return <p className="muted small">Nenhum link de pagamento ainda.</p>;
  return (
    <div className="links-list">
      {erro && <Alert>{erro}</Alert>}
      {links.map((l) => {
        const st = STATUS_LINK[l.status];
        return (
          <div key={l.id} className={`links-list__item ${l.status === 'PAGO' ? 'is-paid' : ''}`}>
            <div className="links-list__main">
              <span className={`tag-pill tag-pill--${st.tone}`}>{st.label}</span>
              <strong>{formatBRL(l.valorCartao)}</strong>
              <span className="muted small">
                {l.venda && (
                  <>
                    <Link to={`/vendas/${l.venda.id}`}>Venda #{l.venda.numero ?? '—'}</Link>
                    {' · '}
                  </>
                )}
                {l.descricao}
                {mostrarCliente && l.lead && (
                  <>
                    {' · '}
                    <Link to={`/clientes/${l.lead.id}`}>{l.lead.nome}</Link>
                  </>
                )}
                {mostrarConsultor && <> · {l.consultor}</>}
              </span>
              <span className="muted small">
                {l.status === 'PAGO' && l.pagoEm ? (
                  <>
                    Pago em {formatDateTime(l.pagoEm)}
                    {l.parcelasPagas ? ` · ${l.parcelasPagas}x` : ''}
                    {l.valorLiquido ? ` · agência recebe ${formatBRL(l.valorLiquido)}` : ''}
                  </>
                ) : (
                  <>
                    Gerado em {formatDateTime(l.createdAt)}
                    {l.status === 'AGUARDANDO' && ` · vale até ${formatDateTime(l.expiraEm)}`}
                  </>
                )}
              </span>
            </div>
            <div className="row tight">
              {l.status === 'PAGO' && l.temParcela && l.baixa?.em && <span className="links-list__baixa ok">✓ Baixa no Conta Azul</span>}
              {l.status === 'PAGO' && l.temParcela && !l.baixa?.em && (
                <>
                  <span className="links-list__baixa err">{l.baixa?.erro ? `Baixa pendente: ${l.baixa.erro}` : 'Registrando a baixa no Conta Azul…'}</span>
                  <Button variant="secondary" loading={busy === l.id} onClick={() => void refazer(l.id)}>
                    Tentar a baixa de novo
                  </Button>
                </>
              )}
              {l.status === 'PAGO' && !l.temParcela && <EscolherVenda link={l} onDone={onChanged} />}
              {l.status !== 'PAGO' && l.status !== 'EXPIRADO' && (
                <Button variant="secondary" loading={busy === l.id} onClick={() => void conferir(l.id)}>
                  Conferir pagamento
                </Button>
              )}
              {l.status === 'AGUARDANDO' && (
                <Button variant="ghost" onClick={() => void copiar(l)}>
                  {copiado === l.id ? 'Copiado!' : 'Copiar link'}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface VendaOpcao {
  id: string;
  numero: number | null;
  clienteNome: string;
  valor: string;
  dataVenda: string;
}

/** Link pago sem venda ligada: escolher a venda e a parcela para dar baixa no Conta Azul. */
function EscolherVenda({ link, onDone }: { link: LinkPagamentoItem; onDone: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [vendas, setVendas] = useState<VendaOpcao[] | null>(null);
  const [vendaId, setVendaId] = useState('');
  const [parcelas, setParcelas] = useState<ParcelaVenda[] | null>(null);
  const [parcelaId, setParcelaId] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function abrir() {
    setAberto(true);
    setErro(null);
    try {
      setVendas(await api.get<VendaOpcao[]>(`/crm/payment-links/${link.id}/vendas`));
    } catch (e) {
      setErro(errorMessage(e));
    }
  }

  async function escolherVenda(id: string) {
    setVendaId(id);
    setParcelas(null);
    setParcelaId('');
    if (!id) return;
    try {
      const ps = await api.get<ParcelaVenda[]>(`/crm/sales/${id}/parcelas`);
      setParcelas(ps);
      const abertas = ps.filter((p) => p.aberta);
      if (abertas.length === 1 && abertas[0]) setParcelaId(abertas[0].id);
    } catch (e) {
      setErro(errorMessage(e));
    }
  }

  async function confirmar() {
    if (!vendaId || !parcelaId) return setErro('Escolha a venda e a parcela.');
    setBusy(true);
    setErro(null);
    try {
      await api.post(`/crm/payment-links/${link.id}/venda`, { saleId: vendaId, parcelaId });
      setAberto(false);
      onDone();
    } catch (e) {
      setErro(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (!aberto) {
    return (
      <Button onClick={() => void abrir()}>
        Escolher a venda
      </Button>
    );
  }
  return (
    <div className="links-list__pick">
      {erro && <Alert>{erro}</Alert>}
      <label className="field">
        <span>Venda do Conta Azul</span>
        <select value={vendaId} onChange={(e) => void escolherVenda(e.target.value)}>
          <option value="">{vendas ? 'Escolha a venda…' : 'Carregando…'}</option>
          {vendas?.map((v) => (
            <option key={v.id} value={v.id}>
              #{v.numero ?? '—'} · {v.clienteNome} · {formatBRL(v.valor)}
            </option>
          ))}
        </select>
      </label>
      {parcelas && (
        <label className="field">
          <span>Parcela</span>
          <select value={parcelaId} onChange={(e) => setParcelaId(e.target.value)}>
            <option value="">Escolha a parcela…</option>
            {parcelas.map((p) => (
              <option key={p.id} value={p.id} disabled={!p.aberta}>
                {p.indice} · {nomeMetodo(p.metodo)} · {p.aberta ? `em aberto ${formatBRL(p.naoPago.toFixed(2))}` : 'paga'}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="row tight">
        <Button loading={busy} onClick={() => void confirmar()}>
          Dar baixa no Conta Azul
        </Button>
        <Button variant="ghost" onClick={() => setAberto(false)}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
